<template>
  <EmPageWrapper class="bg-bgprimary">
    <div class="d-flex flex-column height-100">
      <div class="flex-grow-1">
        <div v-if="readableUser.loadingMenus" class="height-100 d-flex justify-center align-center">
          <v-card>
            <v-card-text>
              <EmLoadingSpinner text="Loading" />
            </v-card-text>
          </v-card>
        </div>
        <div v-else-if="phoneMenus.length !== 0">
          <div v-if="display.mdAndUp" class="mb-10 d-flex justify-space-between align-center">
            <div>
              <div>
                <EmLogo />
              </div>
              <div>
                <EmBody2
                  semi-bold
                  class="text-neutral-lighten-2"
                  data-cy="index-welcome"
                >
                  Welcome to Emissary.ai!
                </EmBody2>
              </div>
            </div>
          </div>
          <div class="d-flex align-center" :class="display.mobile ? 'mb-4': 'mb-10'">
            <EmAccountIcon color="neutral" />
            <EmBody1 v-if="display.mobile">
              {{ currentTenant?.name }}
            </EmBody1>
            <EmH3 v-else>
              {{ currentTenant?.name }}
            </EmH3>
          </div>
          <div
            v-for="(phoneMenu, phoneNumberIndex) in phoneMenus"
            :key="`phone-menu-${phoneMenu.phone_number}`"
            class="mb-10"
          >
            <EmInboxPhoneNumber
              class="mb-6"
              :phone-number="phoneMenu.phone_number"
              :is-primary="phoneMenu.is_seat"
              :inbox-name="phoneMenu.inbox_name"
            />
            <v-row>
              <v-col
                v-for="(menu,index) in phoneMenu.menus"
                :key="`phone-${phoneMenu.phone_number}-sub-menu-${menu.title}`"
                cols="12"
                md="6"
                lg="4"
                xl="3"
              >
                <v-card class="height-100" @click="navigateTo(menu.to)">
                  <v-card-text>
                    <div class="d-flex justify-space-between align-center">
                      <div class="d-flex align-center">
                        <EmIcon color="primary" class="ma-4" :icon="menu.icon" />
                        <EmBody1
                          bold
                          class="text-primary lh-24-px"
                          data-cy="index-menus"
                        >
                          {{ menu.title }}
                        </EmBody1>
                      </div>
                      <EmIconButton
                        x-large
                        color="primary"
                        :title="`Go to ${menu.title.toLowerCase()}`"
                        icon="mdi-arrow-right-bold"
                        :data-cy-form-field="`${phoneNumberIndex}-${index}-go-to-${menu.title.toLowerCase()}`"
                      />
                    </div>
                    <div>
                      <EmBody2>
                        {{ menu.description }}
                      </EmBody2>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </div>
          <EmDivider class="mb-10" />
          <div>
            <v-row>
              <v-col
                v-for="(menu, idx) in globalRoutesInHome"
                :key="`global-menu-${idx}`"
                cols="12"
                md="6"
                lg="4"
                xl="3"
              >
                <v-card
                  class="height-100"
                  @click="navigateTo(menu.to)"
                >
                  <v-card-text>
                    <div class="d-flex justify-space-between align-center">
                      <div class="d-flex align-center">
                        <EmIcon color="primary" class="ma-4" :icon="menu.icon" />
                        <EmBody1 bold class="text-primary">
                          {{ menu.title }}
                        </EmBody1>
                      </div>
                      <EmIconButton
                        x-large
                        color="primary"
                        :title="`Go to ${menu.title}`"
                        icon="mdi-arrow-right-bold"
                        :data-cy-form-field="`go-to-${menu.title.toLowerCase()}`"
                      />
                    </div>
                    <div>
                      <EmBody2>
                        {{ menu.description }}
                      </EmBody2>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </div>
        </div>
        <div v-else>
          <div class="mb-4">
            <EmLogo />
          </div>
          <div>
            <EmBody1
              semi-bold
              class="text-neutral-lighten-2"
              data-cy="index-welcome"
            >
              Welcome to Emissary.ai!
            </EmBody1>
          </div>
          <div>
            <EmBody2 semi-bold>
              This login is not associated with any service on Emissary. Contact <EmSupportEmail /> for details.
            </EmBody2> <br>
            <EmBody2 semi-bold>
              Note that you are logged in as
            </EmBody2>
            <EmBody2 class="text-neutral-lighten-1" bold>
              {{ userName }} - {{ userEmail }}.
            </EmBody2>
          </div>
        </div>
      </div>
      <div v-if="!readableUser.loadingMenus" class="flex-grow-0">
        <div
          v-if="!isMobileDevice"
          class="align-center flex-grow-0"
          :class="display.xs ? 'justify-center' : 'd-flex justify-end'"
        >
          <EmAppStoreRedirectDownloadButtons
            :show-buttons-vertically="display.xs"
            class="my-8"
          />
        </div>
      </div>
    </div>
  </EmPageWrapper>
</template>
<script setup>
import { useDisplay } from 'vuetify'
const display = ref(useDisplay())
useHead({ title: 'Home' })
const { readable: deviceStoreReadable } = useDeviceStore()
const notification = usePushNotificationStore()
const readableUser = useUserStore().readable
const { currentTenant, globalRoutes } = useUserMenuOptions()
const globalRoutesInHome = globalRoutes.value.filter(route => route.showInHome)
const { path } = useRouteParams()
const lastIndexOfNumberSign = path.value.lastIndexOf('#')
if (lastIndexOfNumberSign !== -1) {
  navigateTo(path.value.substr(lastIndexOfNumberSign + 1))
}
const userName = computed(() => readableUser.userInfo?.name)
const userEmail = computed(() => readableUser.userInfo?.email)
const phoneMenus = computed(() => readableUser.phoneMenus || [])
const isMobileDevice = computed(() => deviceStoreReadable.devicePrefs.isMobileDevice)
onMounted(() => {
  nextTick(() => notification.promptToAskForNotificationsPermission())
})
</script>
